import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ExportButton } from '@/components/atoms/ExportButton/ExportButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { type HumanAgentsMetricKey } from './humanAgentsChartConfig';
import { HumanAgentsTimeseriesChart } from './HumanAgentsTimeseriesChart';
import { useComparisonMode } from '../../hooks/useComparisonMode';
import { useHumanAgentsExport } from '../../hooks/useHumanAgentsExport';
import {
  useHumanAgentsOverview,
  type HumanAgentsOverviewData,
} from '../../hooks/useHumanAgentsOverview';
import { useHumanAgentsTimeseries } from '../../hooks/useHumanAgentsTimeseries';
import { selectAnalyticsDates } from '../../redux/selectors';
import {
  formatCountDelta,
  formatCsatDelta,
  formatDurationDelta,
} from '../../utils/deltaFormatters';
import { formatDateRangeLabel, isSingleDayRange } from '../../utils/formatDateRangeLabel';
import { formatDurationFromSeconds } from '../../utils/formatDuration';
import { getComparisonDates } from '../../utils/getComparisonDates';
import { AbsoluteChangeIndicator } from '../aiAgents/AbsoluteChangeIndicator';
import { AnalyticsEmptyState } from '../aiAgents/AnalyticsEmptyState';
import { getComparisonPeriodKey } from '../aiAgents/PercentChangeIndicator';
import { KpiTile } from '../KpiTile/KpiTile';

const formatCsat = (value: number | null | undefined): string =>
  value == null ? '–' : `${value.toFixed(1)}/5`;

const formatCount = (count: number | null | undefined): string =>
  count == null ? '–' : count.toLocaleString();

/** The CSAT metrics show how many conversations carry that score, under the chart. */
const RATED_COUNT_FIELD: Partial<
  Record<HumanAgentsMetricKey, { labelKey: string; field: keyof HumanAgentsOverviewData }>
> = {
  userCsat: {
    labelKey: 'analytics.human_agents_overview.conversations_users_rated',
    field: 'user_rating_count',
  },
  aiCsat: {
    labelKey: 'analytics.human_agents_overview.conversations_ai_rated',
    field: 'csat_human_agent_count',
  },
};

interface MetricTileDef {
  key: HumanAgentsMetricKey;
  label: string;
  tooltip: string;
  value: string;
  current: number | null | undefined;
  previous: number | null | undefined;
  isMetricEmpty: boolean;
  formatDelta: (delta: number) => string;
  precision: number;
  invertColors?: boolean;
}

export const HumanAgentsOverview = () => {
  const { t } = useTranslation();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const { isSingleDay, dateRange } = useMemo(
    () => ({
      isSingleDay: isSingleDayRange(startDate, endDate),
      dateRange: formatDateRangeLabel(startDate, endDate),
    }),
    [startDate, endDate]
  );

  const [selectedMetric, setSelectedMetric] = useState<HumanAgentsMetricKey>('totalConversations');

  const { data: overviewData, comparisonData, isLoading } = useHumanAgentsOverview();
  const { data: timeseries, comparisonData: comparisonTimeseries } = useHumanAgentsTimeseries();
  const { exportWidget, isWidgetExportable, isExporting } = useHumanAgentsExport();

  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));

  // Drive the chart x-axis from the selected range (and the previous-period
  // range when comparing) so every day renders even without returned data.
  const period = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const comparisonPeriod = useMemo(() => {
    const { startDate: start, endDate: end } = getComparisonDates(startDate, endDate);
    return { start, end };
  }, [startDate, endDate]);

  const isEmpty =
    !isLoading &&
    overviewData?.avg_csat_human_agent == null &&
    overviewData?.avg_user_rating == null &&
    overviewData?.avg_resolution_seconds == null &&
    !overviewData?.total_conversations;

  const tiles = useMemo<MetricTileDef[]>(
    () => [
      {
        key: 'totalConversations',
        label: t('analytics.total_conversations'),
        tooltip: t('analytics.human_agents_overview.total_conversations_tooltip'),
        value: formatCount(overviewData?.total_conversations),
        current: overviewData?.total_conversations,
        previous: comparisonData?.total_conversations,
        isMetricEmpty: !overviewData?.total_conversations,
        formatDelta: formatCountDelta,
        precision: 0,
      },
      {
        key: 'resolutionTime',
        label: t('analytics.human_agents_overview.avg_resolution_time'),
        tooltip: t('analytics.human_agents_overview.avg_resolution_time_tooltip'),
        value: formatDurationFromSeconds(overviewData?.avg_resolution_seconds),
        current: overviewData?.avg_resolution_seconds,
        previous: comparisonData?.avg_resolution_seconds,
        isMetricEmpty: overviewData?.avg_resolution_seconds == null,
        formatDelta: formatDurationDelta,
        precision: 0,
        invertColors: true,
      },
      {
        key: 'userCsat',
        label: t('analytics.human_agents_overview.avg_user_csat_score'),
        tooltip: t('analytics.human_agents_overview.avg_user_csat_score_tooltip'),
        value: formatCsat(overviewData?.avg_user_rating),
        current: overviewData?.avg_user_rating,
        previous: comparisonData?.avg_user_rating,
        isMetricEmpty: overviewData?.avg_user_rating == null,
        formatDelta: formatCsatDelta,
        precision: 1,
      },
      {
        key: 'aiCsat',
        label: t('analytics.human_agents_overview.avg_ai_csat_score'),
        tooltip: t('analytics.human_agents_overview.avg_ai_csat_score_tooltip'),
        value: formatCsat(overviewData?.avg_csat_human_agent),
        current: overviewData?.avg_csat_human_agent,
        previous: comparisonData?.avg_csat_human_agent,
        isMetricEmpty: overviewData?.avg_csat_human_agent == null,
        formatDelta: formatCsatDelta,
        precision: 1,
      },
    ],
    [overviewData, comparisonData, t]
  );

  const isSelectedMetricEmpty = tiles.find((tile) => tile.key === selectedMetric)?.isMetricEmpty;

  const ratedCount = RATED_COUNT_FIELD[selectedMetric];

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <CardTitle>{t('analytics.human_agents_overview.title')}</CardTitle>
            <CardDescription>{dateRange}</CardDescription>
          </div>
          {isWidgetExportable('human_agents_overview') && (
            <ExportButton
              variant="icon"
              onExport={(format) => exportWidget('human_agents_overview', format)}
              isExporting={isExporting}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="grid grid-cols-4 gap-4">
            {tiles.map((tile) => (
              <Skeleton key={tile.key} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {tiles.map((tile) => (
              <KpiTile
                key={tile.key}
                label={tile.label}
                tooltip={tile.tooltip}
                value={tile.value}
                isSelected={selectedMetric === tile.key}
                onClick={() => setSelectedMetric(tile.key)}
                comparison={
                  isComparing && !tile.isMetricEmpty ? (
                    <AbsoluteChangeIndicator
                      current={tile.current}
                      previous={tile.previous}
                      formatDelta={tile.formatDelta}
                      precision={tile.precision}
                      comparisonLabel={comparisonLabel}
                      invertColors={tile.invertColors}
                      className="mt-1"
                    />
                  ) : undefined
                }
              />
            ))}
          </div>
        )}
        {isEmpty && (
          <div className="flex items-center justify-center py-6">
            <AnalyticsEmptyState
              title={t('analytics.no_data_available')}
              description={t('analytics.no_data_for_period')}
            />
          </div>
        )}
        {!isEmpty && !isSingleDay && isSelectedMetricEmpty && (
          <div className="flex aspect-4/1 w-full items-center justify-center">
            <AnalyticsEmptyState
              title={t('analytics.no_data_available')}
              description={t('analytics.no_data_for_period')}
            />
          </div>
        )}
        {!isEmpty && !isSingleDay && !isSelectedMetricEmpty && (
          <HumanAgentsTimeseriesChart
            data={timeseries}
            comparisonData={isComparing ? comparisonTimeseries : undefined}
            metric={selectedMetric}
            t={t}
            period={period}
            comparisonPeriod={comparisonPeriod}
          />
        )}
        {!isEmpty && !isLoading && ratedCount && (
          <div className="flex items-baseline gap-2 text-sm leading-5">
            <span className="text-muted-foreground">{t(ratedCount.labelKey)}</span>
            <span className="font-medium text-foreground tabular-nums">
              {`${formatCount(overviewData?.[ratedCount.field] ?? 0)} / ${formatCount(overviewData?.total_conversations ?? 0)}`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
